import { Box, Stack, useTheme } from "@mui/material";
import { Button, FoodsIcon, FoodsTabs, FoodsTabTemplateOption, Typography } from "@foodpilot/foods";
import { Link, Navigate, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { Header } from "./Header";
import { useActionsContext } from "src/context/Actions/useActionsContext";
import { MainContentLayout } from "../Layout/MainContentLayout";
import { ActionPlanMainTabs, OneInLocalStorage } from "src/services/OneFromLocalStorage";
import { Loading } from "../utils/Loading";
import { useQueryClient } from "react-query";
import { getOneActionPlanKey } from "src/api/client-api/ActionPlan/ActionPlan/useGetOneActionPlan";
import { ActionLoadingState } from "./Common/ActionLoadingState";
import { URLHandler } from "src/services/URLHandler";
import { useCompanyMetadata } from "src/context/Company/Metadata/useCompanyMetadata";
import { MainLayoutPaddingFree } from "../Layout/MainLayoutPaddingFree";
import { StaleActionPlanModal } from "src/components/Actions/StaleActionPlan/StaleActionPlanModal";

export type MainLayoutProps = {
    selectedSection?: ActionPlanMainTabs;
};

export const MainLayout = (props: MainLayoutProps) => {
    const { selectedSection } = props;
    const { t } = useTranslation();
    const theme = useTheme();

    const queryClient = useQueryClient();
    const { company, ssqVersion } = useCompanyMetadata();
    const [staleActionPlanModalOpen, setStaleActionPlanModalOpen] = useState(false);

    const { urls, actionPlan, isLoading, isFetched, actionPlanSavingStatus } = useActionsContext();

    useEffect(() => {
        if (actionPlan?.staleStatus) {
            setStaleActionPlanModalOpen(true);
        }
    }, [actionPlan?.staleStatus]);

    // It bothers me to use that, but it looks like the new way has a flaw without that.
    // If you click outside the `label` that has the link, it won't use the anchor.
    const navigate = useNavigate();

    if (isLoading === true) {
        return <Loading />;
    }

    const shouldCreateActionPlan = actionPlan === null;

    if (isFetched) {
        if (shouldCreateActionPlan && selectedSection !== undefined) {
            const url = urls.noActionPlan();
            return <Navigate to={url} replace />;
        }
        if (shouldCreateActionPlan === false && selectedSection === undefined) {
            const url = urls.planning();
            return <Navigate to={url} replace />;
        }
    }

    const tabs: FoodsTabTemplateOption[] = [];

    const planningUrl = urls.planning();
    tabs.push({
        id: "planning",
        name: (
            <Link
                to={planningUrl}
                style={{
                    textDecoration: "none",
                    color: "inherit",
                }}
            >
                <Stack flexDirection="row" alignItems={"center"} gap={theme.spacing(1)}>
                    <FoodsIcon icon="actionPlan" size={3} />
                    <Typography variant="body-bold">{t("actionplan.tabtitle", "Planning")}</Typography>
                </Stack>
            </Link>
        ),
        TabProps: {
            onClick: () => {
                OneInLocalStorage.setItem("actionPlan-main-tabs", "planning");
                navigate(planningUrl);
            },
            disabled: selectedSection === undefined,
        },
    });

    const trajectoryUrl = urls.trajectory();
    tabs.push({
        id: "trajectory",
        name: (
            <Link
                to={trajectoryUrl}
                style={{
                    textDecoration: "none",
                    color: "inherit",
                }}
            >
                <Stack flexDirection="row" alignItems={"center"} gap={theme.spacing(1)}>
                    <FoodsIcon icon="graph" size={2} />
                    <Typography variant="body-bold">{t("actionplan.trajectory.tab", "Trajectory")}</Typography>
                </Stack>
            </Link>
        ),
        TabProps: {
            onClick: () => {
                OneInLocalStorage.setItem("actionPlan-main-tabs", "trajectory");
                queryClient.invalidateQueries([getOneActionPlanKey]);
                navigate(trajectoryUrl);
            },
            disabled: selectedSection === undefined,
        },
    });

    const catalogUrl = urls.catalog();
    tabs.push({
        id: "catalog",
        name: (
            <Link
                to={catalogUrl}
                style={{
                    textDecoration: "none",
                    color: "inherit",
                }}
            >
                <Stack flexDirection="row" alignItems={"center"} gap={theme.spacing(1)}>
                    <FoodsIcon icon="list" size={5} />
                    <Typography variant="body-bold">{t("actionplan.catalog.tab", "Action catalog")}</Typography>
                </Stack>
            </Link>
        ),
        TabProps: {
            onClick: () => {
                OneInLocalStorage.setItem("actionPlan-main-tabs", "catalog");
                navigate(catalogUrl);
            },
            disabled: selectedSection === undefined,
        },
    });

    const selectedTab = tabs.findIndex((i) => i.id === selectedSection);
    return (
        <Box component="div" sx={{ mx: "auto", width: "100%", minWidth: 0, maxWidth: "100%" }}>
            {actionPlan && (
                <StaleActionPlanModal
                    actionPlan={actionPlan}
                    isOpen={staleActionPlanModalOpen}
                    setOpen={setStaleActionPlanModalOpen}
                />
            )}
            <Box
                sx={{
                    background: theme.palette.background.paper,
                    px: { xs: 2, md: 6 },
                    pt: { xs: 8, md: 2 },
                    pb: 0,
                    minWidth: 0,
                    position: "relative",
                    borderBottom: `1px solid ${theme.custom.grey[500]}`,
                }}
            >
                {/* Desktop layout */}
                <Stack
                    direction="row"
                    alignItems="center"
                    gap={theme.spacing(1)}
                    sx={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        [theme.breakpoints.down("md")]: {
                            display: "none",
                        },
                    }}
                >
                    <Header />
                </Stack>

                <Box
                    sx={{
                        display: { xs: "block", md: "none" },
                        width: "100%",
                        minWidth: 0,
                        mb: 2,
                        textAlign: "center",
                        whiteSpace: "normal",
                        overflowWrap: "anywhere",
                    }}
                >
                    <Typography variant="h1">{t("page.trajectory.title")}</Typography>
                </Box>

                <Box sx={{ width: "fit-content", maxWidth: "100%" }}>
                    <Link
                        to={
                            ssqVersion === undefined ?
                                URLHandler.company.overview(company.id)
                            :   URLHandler.company.overviewSsq(company.id, ssqVersion.ssq.id)
                        }
                        style={{
                            color: theme.palette.primary[600],
                        }}
                    >
                        <Button
                            variant="text"
                            startIcon={
                                <Box color={theme.palette.primary[600]}>
                                    <FoodsIcon size={2} icon={"arrowLeft"} />
                                </Box>
                            }
                        >
                            <Typography variant="cta" sx={{ color: theme.palette.primary[600] }}>
                                {t("company.dashboard.go_back_to_dashboard", "Go back to dashboard")}
                            </Typography>
                        </Button>
                    </Link>
                </Box>

                <Box minWidth={0} maxWidth="100%">
                    <FoodsTabs forceScrollable tabs={tabs} selectedTabIndex={selectedTab ?? 0} tabVariant={"primary"} />
                </Box>
            </Box>
            {actionPlanSavingStatus.isSaving ?
                <MainContentLayout>
                    <ActionLoadingState />
                </MainContentLayout>
            : selectedSection === "catalog" ?
                <MainLayoutPaddingFree />
            :   <MainContentLayout />}
        </Box>
    );
};
